/**
 * Workspace Bootstrap Draft
 *
 * Lets a user type in the chat composer while the workspace is
 * still loading, then hands the draft to the real composer once
 * the app is ready.
 *
 * Primary responsibilities:
 * - Persist the in-progress draft across the bootstrap gate
 * - Record an explicit "send when ready" intent (Enter during load)
 * - Expire stale drafts so an old tab never auto-sends hours later
 *
 * Dependencies:
 * - sessionStorage (browser only)
 */

export interface WorkspaceBootstrapDraft {
  text: string
  queuedSend: boolean
  updatedAt: number
}

const STORAGE_KEY = 'losi:workspace-bootstrap-draft:v1'
// A queued send is an explicit intent, but it goes stale: after
// this window the text is restored as a draft instead of sending.
const QUEUED_SEND_TTL_MS = 5 * 60 * 1000
const DRAFT_TTL_MS = 30 * 60 * 1000

// --------------

export function readWorkspaceBootstrapDraft(): WorkspaceBootstrapDraft | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<WorkspaceBootstrapDraft>
    const text = typeof parsed.text === 'string' ? parsed.text : ''
    const updatedAt = Number.isFinite(Number(parsed.updatedAt))
      ? Number(parsed.updatedAt)
      : 0

    if (!text.trim() || !updatedAt) return null
    if (Date.now() - updatedAt > DRAFT_TTL_MS) {
      clearWorkspaceBootstrapDraft()
      return null
    }

    return {
      text,
      // Downgrade a stale queued send to a plain draft restore.
      queuedSend:
        Boolean(parsed.queuedSend) &&
        Date.now() - updatedAt <= QUEUED_SEND_TTL_MS,
      updatedAt
    }
  } catch {
    return null
  }
}

// --------------

export function writeWorkspaceBootstrapDraft(
  draft: Omit<WorkspaceBootstrapDraft, 'updatedAt'>
) {
  if (typeof window === 'undefined') return

  try {
    if (!draft.text.trim() && !draft.queuedSend) {
      clearWorkspaceBootstrapDraft()
      return
    }

    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...draft, updatedAt: Date.now() })
    )
  } catch {
    // Storage can be unavailable (private mode, quota). Typing in
    // the loading composer must never throw because of it.
  }
}

// --------------

export function clearWorkspaceBootstrapDraft() {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage cleanup issues.
  }
}
